/**
 * Staff QR Manager — Register staff, generate QR codes, view shift status
 * Used in hotel owner/manager dashboard
 */
import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Users, Plus, QrCode, Sparkles, Wrench, BedDouble,
  Copy, Check, Clock, Shield, Wallet, Download,
  ChevronDown, X, Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

const DEPT_OPTIONS = [
  { value: 'housekeeping', label: 'Housekeeping', icon: Sparkles, color: 'text-emerald-400' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'text-amber-400' },
  { value: 'front_desk', label: 'Front Desk', icon: BedDouble, color: 'text-blue-400' },
];

function StaffQRCard({ staff, baseUrl, onCopy }) {
  const [showQR, setShowQR] = useState(false);
  const qrUrl = `${baseUrl}/staff?token=${staff.qr_token}`;
  const dept = DEPT_OPTIONS.find(d => d.value === staff.department) || DEPT_OPTIONS[2];
  const DeptIcon = dept.icon;

  return (
    <div className="bg-card border border-border rounded-xl p-4 transition-all hover:shadow-sm" data-testid={`staff-card-${staff.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center bg-muted", dept.color)}>
            <DeptIcon className="w-5 h-5" />
          </div>
          <div>
            <p className="font-semibold text-sm">{staff.name}</p>
            <p className="text-xs text-muted-foreground">{staff.email}</p>
            <div className="flex gap-1.5 mt-1">
              <Badge variant="secondary" className="text-[9px] capitalize">{staff.department.replace('_', ' ')}</Badge>
              {staff.wallet_created && (
                <Badge className="text-[9px] bg-emerald-500/10 text-emerald-600">
                  <Wallet className="w-2 h-2 mr-0.5" /> Wallet
                </Badge>
              )}
              <Badge variant="outline" className="text-[9px]">
                <Shield className="w-2 h-2 mr-0.5" /> Rep: {staff.reputation_score || 0}
              </Badge>
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowQR(!showQR)}
          className="h-8 text-xs gap-1" data-testid={`show-qr-${staff.id}`}>
          <QrCode className="w-3.5 h-3.5" />
          {showQR ? 'Hide QR' : 'Show QR'}
        </Button>
      </div>

      {showQR && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-col items-center gap-3">
            <div className="bg-white p-3 rounded-xl">
              <QRCodeSVG value={qrUrl} size={180} level="M" />
            </div>
            <p className="text-[10px] text-muted-foreground text-center max-w-[220px]">
              {staff.name} scans this QR → opens their personal dashboard on phone
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-[10px]"
                onClick={() => { navigator.clipboard.writeText(qrUrl); onCopy(); }}
                data-testid={`copy-qr-${staff.id}`}>
                <Copy className="w-3 h-3 mr-1" /> Copy Link
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
        <div className="flex gap-3 text-[10px] text-muted-foreground">
          <span>Tasks done: <strong className="text-foreground">{staff.tasks_completed || 0}</strong></span>
          <span>Shift: <strong className="text-foreground">{staff.shift?.start} - {staff.shift?.end}</strong></span>
        </div>
        <Badge variant="outline" className={cn("text-[8px]", staff.status === 'active' ? 'text-emerald-600' : 'text-red-500')}>
          {staff.status}
        </Badge>
      </div>
    </div>
  );
}

export function StaffQRManager({ hotelId }) {
  const [staffList, setStaffList] = useState([]);
  const [shiftStatus, setShiftStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('housekeeping');

  const baseUrl = window.location.origin;

  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/${hotelId}/list`);
      const data = await res.json();
      setStaffList(data.staff || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hotelId]);

  const fetchShifts = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/${hotelId}/shift-status`);
      const data = await res.json();
      setShiftStatus(data);
    } catch { /* ignore */ }
  }, [hotelId]);

  useEffect(() => { fetchStaff(); fetchShifts(); }, [fetchStaff, fetchShifts]);

  const addStaff = async () => {
    if (!name.trim() || !email.trim()) return;
    setAddLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotel_id: hotelId, name, email, department }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${name} registered! QR code ready.`);
        setName(''); setEmail(''); setShowAdd(false);
        fetchStaff();
      }
    } catch { toast.error('Failed to register staff'); }
    finally { setAddLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const grouped = DEPT_OPTIONS.map(d => ({
    ...d,
    staff: staffList.filter(s => s.department === d.value),
  }));

  return (
    <div className="space-y-6" data-testid="staff-qr-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Staff QR Dashboard</h2>
          <p className="text-sm text-muted-foreground">Register staff, generate QR codes, manage shifts</p>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} className="gap-1.5" data-testid="add-staff-btn">
          <Plus className="w-4 h-4" /> Add Staff
        </Button>
      </div>

      {/* Shift Status Bar */}
      {shiftStatus && (
        <div className="flex gap-3" data-testid="shift-status-bar">
          {Object.entries(shiftStatus.departments).map(([dept, info]) => {
            const deptCfg = DEPT_OPTIONS.find(d => d.value === dept);
            const Icon = deptCfg?.icon || Users;
            return (
              <div key={dept} className={cn("flex-1 flex items-center gap-2 bg-card border rounded-xl px-3 py-2",
                info.on_shift ? "border-emerald-500/20" : "border-border")}>
                <div className={cn("w-2 h-2 rounded-full", info.on_shift ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/30")} />
                <Icon className={cn("w-3.5 h-3.5", deptCfg?.color)} />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-medium capitalize">{dept.replace('_', ' ')}</p>
                  <p className="text-[8px] text-muted-foreground">{info.shift_hours} | {info.staff_count} staff</p>
                </div>
                <Badge variant={info.on_shift ? "default" : "secondary"} className="text-[8px]">
                  {info.on_shift ? 'ON' : 'OFF'}
                </Badge>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Staff Form */}
      {showAdd && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="add-staff-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Register New Staff</h3>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="h-6 w-6 p-0">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input placeholder="Full name" value={name} onChange={e => setName(e.target.value)} data-testid="staff-name-input" />
            <Input placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} data-testid="staff-email-input" />
            <select value={department} onChange={e => setDepartment(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="staff-dept-select">
              {DEPT_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <Button onClick={addStaff} disabled={addLoading || !name.trim() || !email.trim()} data-testid="register-staff-btn">
              {addLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Register
            </Button>
          </div>
        </div>
      )}

      {/* Staff by Department */}
      {grouped.map(g => (
        <div key={g.value}>
          <div className="flex items-center gap-2 mb-3">
            <g.icon className={cn("w-4 h-4", g.color)} />
            <h3 className="text-sm font-semibold capitalize">{g.label}</h3>
            <Badge variant="secondary" className="text-[9px]">{g.staff.length}</Badge>
          </div>
          {g.staff.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2 pl-6">No {g.label.toLowerCase()} staff registered</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {g.staff.map(s => (
                <StaffQRCard key={s.id} staff={s} baseUrl={baseUrl} onCopy={() => toast.success('QR link copied!')} />
              ))}
            </div>
          )}
        </div>
      ))}

      {staffList.length === 0 && (
        <div className="text-center py-12">
          <QrCode className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No staff registered yet</p>
          <p className="text-xs text-muted-foreground mt-1">Click "Add Staff" to get started</p>
        </div>
      )}
    </div>
  );
}
